import { useEffect, useMemo, useState } from "react";
import { Chart as ChartJS, ArcElement, Legend, Tooltip } from "chart.js";
import { Pie } from "react-chartjs-2";
import type {
  AssignmentReportPerson,
  AssignmentReportsResponse,
  Project,
  ProjectReportSummaryRow,
  ProjectReportsResponse,
} from "../services/api";
import { fetchAssignmentReports, fetchProjectReports, listProjects } from "../services/api";
import { Modal } from "./Modal";
import { getErrorAlertMessage } from "../utils/errors";
import { PROJECT_STATUS_OPTIONS } from "../constants/projectStatus";

ChartJS.register(ArcElement, Tooltip, Legend);

type ReportType = "delivery" | "status";
type ReportView = "projects" | "assignments";

const CHART_COLORS = [
  "#0b7285",
  "#3a0ca3",
  "#f48c06",
  "#38b000",
  "#e63946",
  "#ff6b6b",
  "#588157",
  "#1d3557",
  "#2a9d8f",
  "#f8a5c2",
  "#b5179e",
  "#4cc9f0",
];

interface FilterState {
  includeExpired: boolean;
  status: string;
  startAfter: string;
  startBefore: string;
}

export function ReportsPage() {
  const [reportView, setReportView] = useState<ReportView>("projects");
  const [reportData, setReportData] = useState<ProjectReportsResponse | null>(null);
  const [assignmentReport, setAssignmentReport] = useState<AssignmentReportsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeReport, setActiveReport] = useState<ReportType | null>(null);
  const [detailGroup, setDetailGroup] = useState<ProjectReportSummaryRow | null>(null);
  const [assignmentDetail, setAssignmentDetail] = useState<AssignmentReportPerson | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    includeExpired: false,
    status: "",
    startAfter: "",
    startBefore: "",
  });
  const [pendingFilters, setPendingFilters] = useState<FilterState>(filters);

  useEffect(() => {
    if (reportView === "projects") {
      void loadReports(filters);
    } else {
      void loadAssignmentReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportView]);

  async function loadReports(appliedFilters: FilterState) {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProjectReports({
        includeExpired: appliedFilters.includeExpired,
        status: appliedFilters.status || undefined,
        startAfter: appliedFilters.startAfter || undefined,
        startBefore: appliedFilters.startBefore || undefined,
      });
      setReportData(data);
    } catch (err) {
      setError(getErrorAlertMessage("load project reports", err));
      try {
        const projects = await listProjects();
        const fallback = buildReportFromProjects(projects, appliedFilters);
        setReportData(fallback);
      } catch (fallbackError) {
        setError(
          (prev) => prev ?? getErrorAlertMessage("load project reports from fallback data", fallbackError),
        );
      }
    } finally {
      setLoading(false);
    }
  }

  async function loadAssignmentReport() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAssignmentReports();
      setAssignmentReport(data);
    } catch (err) {
      setError(getErrorAlertMessage("load assignment reports", err));
    } finally {
      setLoading(false);
    }
  }

  const isProjectView = reportView === "projects";
  const deliverySummary = reportData?.delivery_summary ?? [];
  const statusSummary = reportData?.status_summary ?? [];
  const assignmentPeople = assignmentReport?.people ?? [];
  const assignmentStatusTotals = assignmentReport?.status_totals ?? [];
  const hasAssignmentData = Boolean(assignmentReport && assignmentReport.totals.assignment_count > 0);

  const deliveryChartData = useMemo(() => {
    const labels = deliverySummary.map((item) => item.key);
    const data = deliverySummary.map((item) => item.count);
    const colors = labels.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]);
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: colors,
          borderWidth: 1,
        },
      ],
    };
  }, [deliverySummary]);

  const statusChartData = useMemo(() => {
    const labels = statusSummary.map((item) => item.key);
    const data = statusSummary.map((item) => item.count);
    const colors = labels.map((_, index) => CHART_COLORS[(index + 4) % CHART_COLORS.length]);
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: colors,
          borderWidth: 1,
        },
      ],
    };
  }, [statusSummary]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom" as const,
        },
      },
    }),
    [],
  );

  const hasReports = Boolean(reportData && reportData.totals.project_count > 0);

  function handleCloseModals() {
    setActiveReport(null);
    setDetailGroup(null);
    setAssignmentDetail(null);
  }

  function handleExport() {
    if (!reportData) {
      return;
    }
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "project-reports.json";
    link.click();
    URL.revokeObjectURL(url);
  }

  function applyFilters() {
    setFilters(pendingFilters);
    void loadReports(pendingFilters);
  }

  function summarizeStatusCounts(counts: Record<string, number>): string {
    const entries = Object.entries(counts);
    if (entries.length === 0) {
      return "—";
    }
    return entries
      .sort((a, b) => b[1] - a[1])
      .map(([status, count]) => `${status.replace(/_/g, " ")} (${count})`)
      .join(", ");
  }

  return (
    <div className="reports-page">
      <header className="panel">
        <div className="panel-header">
          <h2>Operational Reports</h2>
          <div className="panel-actions" style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <label style={{ display: "flex", flexDirection: "column", fontSize: "0.85rem" }}>
              Report Type
              <select value={reportView} onChange={(event) => setReportView(event.target.value as ReportView)}>
                <option value="projects">Project Pipeline</option>
                <option value="assignments">Assignments by Person</option>
              </select>
            </label>
            <button
              type="button"
              className="ghost"
              disabled={loading}
              onClick={() => void (isProjectView ? loadReports(filters) : loadAssignmentReport())}
            >
              {loading ? "Refreshing…" : "Refresh Data"}
            </button>
          </div>
        </div>
        <p className="helper-text">
          {isProjectView
            ? "Explore AWS requisition trends visually. Filter the dataset, download JSON, or drill into a delivery unit for more context."
            : "Review how many AWS RR's each consultant is attached to and inspect the underlying assignments."}
        </p>
        {isProjectView ? (
          <div className="form-grid">
            <label>
              Start Before
              <input
                type="date"
                value={pendingFilters.startBefore}
                onChange={(event) => setPendingFilters((prev) => ({ ...prev, startBefore: event.target.value }))}
              />
            </label>
            <label>
              Status Filter
              <select
                value={pendingFilters.status}
                onChange={(event) => setPendingFilters((prev) => ({ ...prev, status: event.target.value }))}
              >
                <option value="">All statuses</option>
                {PROJECT_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Start After
              <input
                type="date"
                value={pendingFilters.startAfter}
                onChange={(event) => setPendingFilters((prev) => ({ ...prev, startAfter: event.target.value }))}
              />
            </label>
            <label className="checkbox-field" style={{ alignItems: "center", justifyContent: "center" }}>
              <span style={{ marginBottom: "0.25rem" }}>Include expired RR&apos;s</span>
              <input
                type="checkbox"
                checked={pendingFilters.includeExpired}
                onChange={(event) => setPendingFilters((prev) => ({ ...prev, includeExpired: event.target.checked }))}
                style={{ alignSelf: "center" }}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="primary" onClick={applyFilters} disabled={loading}>
                Apply Filters
              </button>
              <button type="button" className="ghost" onClick={handleExport} disabled={!reportData}>
                Export JSON
              </button>
            </div>
          </div>
        ) : (
          <p className="helper-text" style={{ marginBottom: 0 }}>
            Use the detail buttons below to see every RR per consultant. Results automatically pull from the Assignments table.
          </p>
        )}
      </header>

      {error && <div className="alert error">{error}</div>}

      {isProjectView ? (
        hasReports ? (
          <>
            {reportData && (
              <section
                className="panel"
                style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
              >
                <MetricCard
                  label="Active RR&apos;s"
                  value={reportData.totals.active_count.toLocaleString()}
                  accent="var(--color-success, #38b000)"
                />
                <MetricCard
                  label="Expired RR&apos;s"
                  value={reportData.totals.expired_count.toLocaleString()}
                  accent="var(--color-danger, #e63946)"
                />
                <MetricCard
                  label="Total Requested Hours"
                  value={reportData.totals.requested_hours_total.toLocaleString()}
                  accent="var(--color-info, #3a0ca3)"
                />
              </section>
            )}

            {reportData && (
              <section className="panel">
                <h3>Pipeline Summary</h3>
                <div className="chip-row">
                  {Object.entries(reportData.pipeline_summary).map(([status, count]) => (
                    <span key={status} className="chip">
                      {status}: {count}
                    </span>
                  ))}
                </div>
              </section>
            )}

            <div className="reports-grid">
              <article className="report-card">
                <h3>Projects by Delivery Unit</h3>
                <p>See how AWS RR&apos;s are distributed across delivery units, with drill-down into requisition titles.</p>
                <button type="button" className="primary" onClick={() => setActiveReport("delivery")} disabled={!hasReports}>
                  View Report
                </button>
              </article>

              <article className="report-card">
                <h3>Projects by Status</h3>
                <p>Understand staffing pipeline health based on the latest requisition status mix.</p>
                <button type="button" className="primary" onClick={() => setActiveReport("status")} disabled={!hasReports}>
                  View Report
                </button>
              </article>
            </div>
          </>
        ) : (
          <section className="panel">{loading ? <p>Loading project reports…</p> : <div className="empty">No project data available.</div>}</section>
        )
      ) : (
        <section className="panel">
          {loading ? (
            <p>Loading assignment insights…</p>
          ) : hasAssignmentData ? (
            <>
              <div className="metrics-grid" style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                <MetricCard
                  label="Total Assignments"
                  value={(assignmentReport?.totals.assignment_count ?? 0).toLocaleString()}
                  accent="var(--color-info, #3a0ca3)"
                />
                <MetricCard
                  label="Consultants In Pipeline"
                  value={(assignmentReport?.totals.person_count ?? 0).toLocaleString()}
                  accent="var(--color-success, #38b000)"
                />
              </div>
              <h3 style={{ marginTop: "1.5rem" }}>Status Mix</h3>
              <div className="chip-row">
                {assignmentStatusTotals.length === 0 ? (
                  <span className="chip">No status data</span>
                ) : (
                  assignmentStatusTotals.map((row) => (
                    <span key={row.status} className="chip">
                      {row.status}: {row.count}
                    </span>
                  ))
                )}
              </div>
              <div className="table-wrapper" style={{ marginTop: "1.5rem" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Consultant</th>
                      <th>Assignments</th>
                      <th>Status Breakdown</th>
                      <th>Latest Activity</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {assignmentPeople.map((person) => (
                      <tr key={person.person_email}>
                        <td>{person.person_email}</td>
                        <td>{person.assignment_count}</td>
                        <td>{summarizeStatusCounts(person.status_counts)}</td>
                        <td>{person.latest_activity ?? "—"}</td>
                        <td>
                          <button type="button" onClick={() => setAssignmentDetail(person)}>
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="empty">No assignments have been recorded yet.</div>
          )}
        </section>
      )}

      <Modal
        open={activeReport !== null}
        onClose={handleCloseModals}
        title={
          activeReport === "delivery"
            ? "AWS RR Distribution by Delivery Unit"
            : activeReport === "status"
              ? "AWS RR Distribution by Status"
              : undefined
        }
      >
        {activeReport === "delivery" && (
          <div className="report-modal">
            {deliverySummary.length === 0 ? (
              <p>No project data available.</p>
            ) : (
              <>
                <div className="report-chart">
                  <Pie data={deliveryChartData} options={chartOptions} />
                </div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Delivery Unit</th>
                        <th>Projects</th>
                        <th>Sample Titles</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {deliverySummary.map((row) => (
                        <tr key={row.key}>
                          <td>{row.key}</td>
                          <td>{row.count}</td>
                          <td>{(row.sample_titles || []).join(", ") || "—"}</td>
                          <td>
                            <button type="button" onClick={() => setDetailGroup(row)}>
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeReport === "status" && (
          <div className="report-modal">
            {statusSummary.length === 0 ? (
              <p>No project data available.</p>
            ) : (
              <>
                <div className="report-chart">
                  <Pie data={statusChartData} options={chartOptions} />
                </div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Projects</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusSummary.map((row) => (
                        <tr key={row.key}>
                          <td>{row.key}</td>
                          <td>{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal open={Boolean(detailGroup)} onClose={() => setDetailGroup(null)} title={detailGroup ? `Delivery Unit: ${detailGroup.key}` : undefined}>
        {detailGroup ? (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>RR</th>
                  <th>Title</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(detailGroup.projects || []).map((project) => (
                  <tr key={project.project_id}>
                    <td>{project.project_id}</td>
                    <td>{project.title ?? "Untitled"}</td>
                    <td>{project.status ?? "Unknown"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">No project details available for this group.</p>
        )}
      </Modal>

      <Modal
        open={Boolean(assignmentDetail)}
        onClose={() => setAssignmentDetail(null)}
        title={assignmentDetail ? `Assignments for ${assignmentDetail.person_email}` : undefined}
      >
        {assignmentDetail ? (
          assignmentDetail.assignments.length === 0 ? (
            <p>This consultant does not have any recorded assignments.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>RR</th>
                    <th>Status</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentDetail.assignments.map((assignment) => (
                    <tr key={assignment.assignment_id ?? `${assignment.project_id}-${assignment.status}`}>
                      <td>{assignment.project_id ?? "Unknown"}</td>
                      <td>{assignment.status ?? "Unknown"}</td>
                      <td>{assignment.start_date ?? "—"}</td>
                      <td>{assignment.end_date ?? "—"}</td>
                      <td>{assignment.updated_at ?? assignment.created_at ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </Modal>
    </div>
  );
}

function MetricCard({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <article
      className="metric-card"
      style={{
        border: `1px solid ${accent}`,
        borderRadius: "12px",
        padding: "1rem",
        textAlign: "center",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)",
      }}
    >
      <p style={{ fontSize: "0.9rem", textTransform: "uppercase", letterSpacing: "0.05em", color: accent, marginBottom: "0.25rem" }}>
        {label}
      </p>
      <p style={{ fontSize: "2rem", fontWeight: 700, color: "var(--text-color, #111)" }}>{value}</p>
    </article>
  );
}

function buildReportFromProjects(projects: Project[], filters: FilterState): ProjectReportsResponse {
  const statuses = PROJECT_STATUS_OPTIONS;

  const filteredProjects = projects.filter((project) => {
    const status = (project.status ?? "").trim().toUpperCase();
    if (!filters.includeExpired && status === "EXPIRED") {
      return false;
    }
    if (filters.status && status !== filters.status) {
      return false;
    }
    const startDate = (project.start_date ?? "").trim();
    if (filters.startAfter && (!startDate || startDate < filters.startAfter)) {
      return false;
    }
    if (filters.startBefore && (!startDate || startDate > filters.startBefore)) {
      return false;
    }
    return true;
  });

  const deliverySummary: Record<string, ProjectReportSummaryRow> = {};
  const statusSummary: Record<string, { key: string; count: number }> = {};
  let requestedHoursTotal = 0;
  let activeCount = 0;
  let expiredCount = 0;

  filteredProjects.forEach((project) => {
    const status = (project.status ?? "UNKNOWN").trim().toUpperCase();
    const delivery = (project.delivery_unit ?? "Unassigned").trim();
    const row = deliverySummary[delivery] ?? { key: delivery, count: 0, sample_titles: [], projects: [] };
    row.count += 1;
    if ((row.sample_titles ?? []).length < 5 && project.title) {
      row.sample_titles = [...(row.sample_titles ?? []), project.title];
    }
    if ((row.projects ?? []).length < 25) {
      row.projects = [
        ...(row.projects ?? []),
        { project_id: project.project_id, title: project.title, status: project.status },
      ];
    }
    deliverySummary[delivery] = row;

    const statusRow = statusSummary[status] ?? { key: status, count: 0 };
    statusRow.count += 1;
    statusSummary[status] = statusRow;

    const requested = Number(project.requested_hours);
    if (!Number.isNaN(requested)) {
      requestedHoursTotal += requested;
    }

    if (status === "EXPIRED") {
      expiredCount += 1;
    } else {
      activeCount += 1;
    }
  });

  const deliveryRows = Object.values(deliverySummary).sort((a, b) => b.count - a.count);
  const statusRows = [
    ...statuses
      .map((status) => statusSummary[status])
      .filter((row): row is { key: string; count: number } => Boolean(row)),
    ...Object.entries(statusSummary)
      .filter(([key]) => !(statuses as readonly string[]).includes(key))
      .map(([, row]) => row),
  ];

  const pipelineSummary: Record<string, number> = {
    SUBMITTED_TO_AWS: statusSummary["SUBMITTED_TO_AWS"]?.count ?? 0,
    INTERVIEW: statusSummary["INTERVIEW"]?.count ?? 0,
    ACCEPTED: statusSummary["ACCEPTED"]?.count ?? 0,
  };

  return {
    totals: {
      project_count: filteredProjects.length,
      requested_hours_total: requestedHoursTotal,
      active_count: activeCount,
      expired_count: expiredCount,
    },
    delivery_summary: deliveryRows,
    status_summary: statusRows,
    pipeline_summary: pipelineSummary,
    filters: {
      include_expired: filters.includeExpired,
      status: filters.status || null,
      start_after: filters.startAfter || null,
      start_before: filters.startBefore || null,
    },
  };
}
